/**
 * The winner sheet for one locked row (ISSUE-1291), after v1's
 * winner_strip_selection.dart: "Winning spot", the row's member and digit,
 * and the row's winners, each with a date and a note. An admin adds one per
 * quarter, half or whatever the group plays for, removes any, then saves the
 * whole list in one call. A member sees the same list read only.
 *
 * Closing with unsaved edits asks first (unsaved-changes guard); only the
 * explicit Discard drops them.
 */
import { Button, confirmDestructive, Dialog } from "@sportsmet/ui";
import { type ReactNode, useEffect, useRef, useState } from "react";

import { todayInZone } from "../scores/calendarDay";
import { resolveViewerTimeZone } from "../scores/viewerTimeZone";
import {
  rowHolderName,
  type typeStripRowView,
  type typeStripView,
} from "./stripBoardModel";
import {
  STRIP_CANCEL_LABEL,
  STRIP_CLOSE_LABEL,
  STRIP_DISCARD_BODY,
  STRIP_DISCARD_LABEL,
  STRIP_DISCARD_TITLE,
  STRIP_WINNER_ADD_LABEL,
  STRIP_WINNER_EMPTY,
  STRIP_WINNER_OPEN_ROW_NOTE,
  STRIP_WINNER_MAX_REACHED,
  STRIP_WINNER_SAVE_LABEL,
  STRIP_WINNER_SHEET_TITLE,
  stripWinnerSheetSubtitle,
} from "./stripCardCopy";
import { stripDateLabel } from "./stripTime";
import { StripWinnerFields } from "./StripWinnerFields";
import {
  buildWinnerDrafts,
  isWinnerDraftDirty,
  newWinnerDraft,
  STRIP_MAX_WINNERS_PER_ROW,
  type typeStripWinnerDraft,
  winnerDraftErrors,
  winnerDraftsBody,
} from "./stripWinnerDraft";

export type typeStripWinnerSheetProps = {
  open: boolean;
  strip: typeStripView | null;
  row: typeStripRowView | null;
  canEdit: boolean;
  isBusy: boolean;
  onClose: () => void;
  onSave: (input: {
    stripNumber: number;
    row: number;
    winners: { stripWinnerId: string | null; wonAt: string; note: string }[];
  }) => Promise<boolean>;
};

export const StripWinnerSheet = (
  props: typeStripWinnerSheetProps,
): ReactNode => {
  const timeZone = resolveViewerTimeZone();
  const { strip, row } = props;
  const [initial, setInitial] = useState<typeStripWinnerDraft[]>([]);
  const [drafts, setDrafts] = useState<typeStripWinnerDraft[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const nextKey = useRef(0);

  // Seed the drafts each time the sheet opens on a row.
  const seedKey = props.open
    ? `${String(strip?.stripNumber)}:${String(row?.row)}`
    : "";
  useEffect(() => {
    if (!props.open || row === null) return;
    const seeded = buildWinnerDrafts({ winners: row.winners, timeZone });
    setInitial(seeded);
    setDrafts(seeded);
    setIsSubmitted(false);
    // Re-seed only when the sheet opens on another row, never on a poll.
  }, [seedKey]);

  if (strip === null || row === null) return null;

  const isDirty = props.canEdit && isWinnerDraftDirty({ initial, drafts });
  const errors = isSubmitted ? winnerDraftErrors(drafts) : {};
  const subtitle = stripWinnerSheetSubtitle({
    stripLabel: strip.label,
    rowLabel: row.rowLabel,
    holder: rowHolderName(row),
    digit: row.digit ?? null,
  });

  async function requestClose(): Promise<void> {
    if (!isDirty) {
      props.onClose();
      return;
    }
    const isConfirmed = await confirmDestructive({
      title: STRIP_DISCARD_TITLE,
      body: STRIP_DISCARD_BODY,
      confirmLabel: STRIP_DISCARD_LABEL,
    });
    if (isConfirmed) props.onClose();
  }

  async function save(): Promise<void> {
    if (strip === null || row === null) return;
    setIsSubmitted(true);
    const winners = winnerDraftsBody(drafts);
    if (winners === null) return;
    const isDone = await props.onSave({
      stripNumber: strip.stripNumber,
      row: row.row,
      winners,
    });
    if (isDone) props.onClose();
  }

  function update(key: string, change: Partial<typeStripWinnerDraft>): void {
    setDrafts((current) =>
      current.map((draft) =>
        draft.key === key ? { ...draft, ...change } : draft,
      ),
    );
  }

  const footer = props.canEdit ? (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          void requestClose();
        }}
      >
        {STRIP_CANCEL_LABEL}
      </Button>
      <Button
        variant="primary"
        data-testid="strip-winners-save"
        loading={props.isBusy}
        disabled={!isDirty}
        onClick={() => {
          void save();
        }}
      >
        {STRIP_WINNER_SAVE_LABEL}
      </Button>
    </>
  ) : (
    <Button variant="secondary" onClick={props.onClose}>
      {STRIP_CLOSE_LABEL}
    </Button>
  );

  return (
    <Dialog
      open={props.open}
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
      title={STRIP_WINNER_SHEET_TITLE}
      description={subtitle}
      autoFocus={props.canEdit && drafts.length > 0}
      isDirty={isDirty}
      onRequestClose={() => {
        void requestClose();
      }}
      footer={footer}
    >
      <div data-testid="strip-winner-sheet" className="flex flex-col gap-4">
        {props.canEdit && (row.userId ?? null) === null ? (
          <p
            className="text-caption text-ink-secondary"
            data-testid="strip-winner-open-row-note"
          >
            {STRIP_WINNER_OPEN_ROW_NOTE}
          </p>
        ) : null}
        {drafts.length === 0 ? (
          <p className="text-body text-ink-secondary">{STRIP_WINNER_EMPTY}</p>
        ) : null}
        {props.canEdit
          ? drafts.map((draft, index) => (
              <StripWinnerFields
                key={draft.key}
                index={index}
                draft={draft}
                error={errors[draft.key]}
                onChange={(change) => {
                  update(draft.key, change);
                }}
                onRemove={() => {
                  setDrafts((current) =>
                    current.filter((candidate) => candidate.key !== draft.key),
                  );
                }}
              />
            ))
          : drafts.map((draft, index) => (
              <div
                key={draft.key}
                data-testid={`strip-winner-read-${String(index)}`}
                className="flex flex-col gap-1 rounded-md border border-line p-3"
              >
                <span className="text-caption text-ink-secondary">
                  {row.winners[index]
                    ? stripDateLabel({
                        iso: row.winners[index].wonAt,
                        timeZone,
                      })
                    : draft.day}
                </span>
                <span className="text-body text-ink whitespace-pre-line">
                  {draft.note}
                </span>
              </div>
            ))}
        {props.canEdit ? (
          drafts.length < STRIP_MAX_WINNERS_PER_ROW ? (
            <Button
              variant="outline"
              data-testid="strip-winner-add"
              onClick={() => {
                nextKey.current += 1;
                setDrafts((current) => [
                  ...current,
                  newWinnerDraft({
                    key: `new-${String(nextKey.current)}`,
                    today: todayInZone({ nowMs: Date.now(), timeZone }),
                  }),
                ]);
              }}
            >
              {STRIP_WINNER_ADD_LABEL}
            </Button>
          ) : (
            <p className="text-caption text-ink-secondary">
              {STRIP_WINNER_MAX_REACHED}
            </p>
          )
        ) : null}
      </div>
    </Dialog>
  );
};
